import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/zoom";
import "./MediaViewer.scss";
import { describeNativeSaveResult, NATIVE_SAVE_PENDING_MESSAGE, NATIVE_SAVE_UNKNOWN_MESSAGE } from "utils/mediaDownload";

// 슬라이드의 신원. 호출부가 key 를 주면 그것이 기준이고,
// 주지 않는 호출부(커뮤니티·프로필)는 URL 을 신원으로 쓴다.
const slideIdentity = (slide, index) => slide?.key ?? slide?.url ?? `idx-${index}`;

// 로드 실패는 "그 자리"가 아니라 "그 리소스"의 실패다. 호출부가 같은 첨부의 URL 을
// 재발급해 넣어주면 다시 시도할 수 있어야 하므로 신원과 URL 을 함께 기억한다.
// 둘 다 컴포넌트 state 를 쓰지 않는 순수 함수라 모듈 스코프에 둔다 — hook deps 에
// 끌려들어오지 않아야 effect 의존성을 정직하게 적을 수 있다.
const slideResource = (slide, index) => `${slideIdentity(slide, index)}::${slide?.url ?? ""}`;

// 저장 상태 { status: "pending" | "pending-long" | "pending-unknown" | "done", result? } → 화면 피드백.
// 실패만 alert 로 끊어 알리고, 성공·장기 대기·결과 미수신은 status 로 알린다. 취소는 침묵.
// 결과를 주지 않는 호출부(result 없음)는 알 수 없음으로 두고 성공이라고 말하지 않는다.
const saveFeedbackOf = (state) => {
    if (!state) return null;
    if (state.status === "pending-long") return { role: "status", message: NATIVE_SAVE_PENDING_MESSAGE };
    if (state.status === "pending-unknown") return { role: "status", message: NATIVE_SAVE_UNKNOWN_MESSAGE };
    if (state.status !== "done") return null;
    const { result } = state;
    if (result && (result.via === "blocked" || result.via === "none")) {
        return { role: "alert", message: "저장하지 못했어요. 잠시 후 다시 시도해 주세요." };
    }
    const described = describeNativeSaveResult(result);
    if (!described || !described.message) return null;
    return { role: described.kind === "permanent" || described.kind === "transient" ? "alert" : "status", message: described.message };
};

// onDownload 는 호출부가 저장 기능을 제공할 때만 넘긴다.
// 넘어오지 않으면 저장 버튼 자체를 렌더하지 않는다(커뮤니티·프로필·리뷰).
// onSlideError 는 열려 있는 동안 로드에 실패한 슬라이드를 호출부가 기억해야 할 때만 넘긴다.
// saveStates 는 호출부가 저장 상태를 슬라이드 key 별로 소유할 때 넘긴다(채팅 — 닫았다 열어도 유지).
// 넘기지 않으면 뷰어가 열려 있는 동안 key 별로 직접 기억한다.
// 슬라이드 메타 `downloadable: false` 는 그 슬라이드에서만 저장 버튼을 감춘다(채팅 dev 사진-only — 영상 저장 미제공).
// 메타가 없으면 기존과 같이 onDownload 유무로만 결정된다.
const MediaViewer = ({ images, media, initialIndex = 0, onClose, title, onDownload, onSlideError, saveStates }) => {
    const slides = useMemo(() => {
        if (media && media.length > 0) return media;
        if (images && images.length > 0) return images.map((url) => ({ url, type: "image" }));
        return [];
    }, [media, images]);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isPaused, setIsPaused] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    // 저장 상태는 자리(index)가 아니라 슬라이드 key 에 귀속된다. 슬라이드를 넘겨도 버리지 않고,
    // 그 슬라이드로 돌아오면 다시 보인다. 호출부가 saveStates 를 넘기면 그것이 기준이다.
    const [localSaveStates, setLocalSaveStates] = useState({});
    const effectiveSaveStates = saveStates ?? localSaveStates;
    const [failedSlides, setFailedSlides] = useState({});
    const videoRefs = useRef({});
    // 재생 컨트롤(wrapper) 자체의 참조. 전환 시 포커스를 이어주려면 요소가 필요하다.
    const videoWrapperRefs = useRef({});
    // 전환 직전 포커스가 나가는 슬라이드의 재생 컨트롤에 있었는가.
    const focusTransferRef = useRef(false);
    // 같은 tick 의 연타를 막는 key 집합 — state 반영 전이라 ref 로 건다.
    const savingKeysRef = useRef(new Set());
    const swiperRef = useRef(null);
    const closeButtonRef = useRef(null);
    const saveButtonRef = useRef(null);
    const dialogRef = useRef(null);

    // onClose 는 호출부에서 인라인 화살표로 넘어오는 경우가 많아 렌더마다 신원이 바뀐다.
    // history/키보드 effect 가 그때마다 재실행되지 않도록 ref 로 최신 값만 들고 있는다.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    // 닫기 경로가 두 갈래(UI/ESC vs popstate)라 상태를 ref 로 고정한다.
    // - historyPushedRef: 우리가 넣은 history state 가 아직 남아 있는가
    // - closedRef: 이미 닫기를 한 번 처리했는가 (중복 navigation 방지)
    const historyPushedRef = useRef(false);
    const closedRef = useRef(false);

    const requestClose = useCallback(() => {
        if (closedRef.current) return;
        closedRef.current = true;
        // 우리가 추가한 state 만 되돌린다. popstate 로 이미 빠져나갔으면 건드리지 않는다.
        if (historyPushedRef.current) {
            historyPushedRef.current = false;
            window.history.back();
        }
        onCloseRef.current?.();
    }, []);

    // 재생 시도의 결과로 정지 표시를 정한다.
    // play() 는 거부될 수 있다(iOS 는 무음이 아닌 자동재생을 항상 막는다). 거부됐는데
    // isPaused=false 로 두면 멈춘 프레임 위에 ▶ 가 없어 조작 방법이 보이지 않는다.
    // isCurrent 는 슬라이드가 이미 넘어간 뒤 늦게 도착한 결과를 버리기 위한 가드다.
    const playVideo = useCallback((video, isCurrent = () => true) => {
        let played;
        try {
            played = video.play();
        } catch {
            setIsPaused(true);
            return;
        }
        if (!played || typeof played.then !== "function") {
            setIsPaused(false);
            return;
        }
        played.then(
            () => { if (isCurrent()) setIsPaused(false); },
            () => { if (isCurrent()) setIsPaused(true); },
        );
    }, []);

    // 현재 슬라이드가 가리키는 리소스. 호출부가 자리를 먼저 잡고 URL 을 나중에 채우면
    // 슬라이드 전환 없이 <video> 가 새로 마운트된다 — 그때도 재생 동기화가 다시 돌아야
    // 실제 재생 상태와 ▶ 표시·aria 가 어긋나지 않는다.
    const currentUrl = slides[currentIndex]?.url;

    // 슬라이드 전환(또는 현재 슬라이드의 URL 도착) 시 이전 동영상 pause, 현재 동영상 play
    useEffect(() => {
        let cancelled = false;
        Object.entries(videoRefs.current).forEach(([idx, video]) => {
            if (!video) return;
            if (Number(idx) === currentIndex) {
                playVideo(video, () => !cancelled);
            } else {
                video.pause();
            }
        });
        setProgress(0);
        return () => { cancelled = true; };
    }, [currentIndex, currentUrl, playVideo]);

    // 저장 안내는 슬라이드 key 별 상태에서 파생되므로 슬라이드를 떠나면 자연히 사라지고
    // 돌아오면 다시 보인다 — 여기서 지우거나 되살릴 것이 없다.
    const currentIndexRef = useRef(currentIndex);
    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    // 호출부는 열려 있는 동안에도 같은 자리의 내용을 바꿀 수 있다(URL 재발급·실패 항목 교체).
    // 그래서 "요청을 시작한 슬라이드"는 index 만으로 식별되지 않는다. 결과가 도착한 시점의
    // 최신 slides 와 대조할 수 있어야 index+신원 둘 다로 귀속을 판단할 수 있다.
    const slidesRef = useRef(slides);
    useEffect(() => {
        slidesRef.current = slides;
    });

    // 현재 비디오 timeupdate로 progress 추적
    useEffect(() => {
        const video = videoRefs.current[currentIndex];
        if (!video) return;
        const onTimeUpdate = () => {
            if (video.duration) {
                setProgress(video.currentTime / video.duration);
                setCurrentTime(video.currentTime);
                setDuration(video.duration);
            }
        };
        const onLoadedMetadata = () => {
            if (video.duration) setDuration(video.duration);
        };
        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("loadedmetadata", onLoadedMetadata);
        if (video.duration) setDuration(video.duration);
        return () => {
            video.removeEventListener("timeupdate", onTimeUpdate);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
        };
        // 같은 이유로 URL 이 늦게 채워진 <video> 에도 리스너를 다시 붙인다.
    }, [currentIndex, currentUrl]);

    // 언마운트 시 모든 동영상 pause
    useEffect(() => {
        const refs = videoRefs.current;
        return () => {
            Object.values(refs).forEach((video) => {
                if (video) video.pause();
            });
        };
    }, []);

    // 스크롤 잠금. 이 앱의 실제 스크롤러는 body 가 아니라 PullToRefresh 의
    // `.ptr-scroll-container` 다(CommonModal 도 같은 셀렉터를 잠근다). body 만 잠그면
    // 뷰어 뒤 화면이 계속 스크롤된다.
    // 복원은 빈 값이 아니라 **진입 시점의 inline 값**으로 되돌린다 — 뷰어가 다른 오버레이
    // 위에서 열렸을 때 빈 값으로 덮으면 그쪽 잠금을 대신 풀어버린다.
    // 잠금 중 스크롤러 위치가 튈 수 있으므로 진입 시점 scrollTop 도 함께 되돌린다.
    useEffect(() => {
        const scroller = document.querySelector(".ptr-scroll-container");
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        const previousScrollerOverflow = scroller ? scroller.style.overflow : "";
        const previousScrollTop = scroller ? scroller.scrollTop : 0;

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        if (scroller) scroller.style.overflow = "hidden";

        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
            if (scroller) {
                scroller.style.overflow = previousScrollerOverflow;
                scroller.scrollTop = previousScrollTop;
            }
        };
    }, []);

    // 모달 진입 시 닫기 버튼으로 포커스를 옮기고, 닫힐 때 이전 포커스를 복원한다.
    useEffect(() => {
        const previouslyFocused = document.activeElement;
        closeButtonRef.current?.focus();
        return () => {
            if (previouslyFocused && typeof previouslyFocused.focus === "function") {
                previouslyFocused.focus();
            }
        };
    }, []);

    // 뒤로가기(브라우저 · Android 하드웨어 back) 대응.
    // 뷰어가 열릴 때 더미 state 를 한 칸 쌓아두고, popstate 가 오면 페이지를 떠나는 대신 뷰어만 닫는다.
    // deps 가 비어 있어야 렌더마다 state 가 쌓이지 않는다.
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.history?.pushState !== "function") return undefined;

        window.history.pushState({ mediaViewer: true }, "");
        historyPushedRef.current = true;

        const handlePopState = () => {
            // 이미 우리 state 를 빠져나온 상태다. 여기서 back() 을 또 부르면 실제 페이지가 밀린다.
            historyPushedRef.current = false;
            if (closedRef.current) return;
            closedRef.current = true;
            onCloseRef.current?.();
        };

        window.addEventListener("popstate", handlePopState);
        // cleanup 은 리스너만 정리한다. 언마운트 시 임의로 navigation 하지 않는다
        // (라우터 이동으로 언마운트된 경우 back() 을 부르면 그 이동을 되돌려버린다).
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    // ESC 닫기 + 포커스 트랩
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                requestClose();
                return;
            }
            if (event.key !== "Tab") return;

            const root = dialogRef.current;
            if (!root) return;
            const focusables = Array.from(
                root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
            ).filter((el) => !el.disabled && el.getAttribute("aria-hidden") !== "true");
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            if (!root.contains(active)) {
                event.preventDefault();
                first.focus();
            } else if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [requestClose]);

    const handleSeek = (e) => {
        const video = videoRefs.current[currentIndex];
        if (!video || !video.duration) return;
        const bar = e.currentTarget;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        video.currentTime = ratio * video.duration;
        setProgress(ratio);
    };

    // 확대 상태에서는 탭을 zoom 토글로 흡수시키므로, 단일 탭으로만 play/pause 제어
    const handleVideoTap = (e, index) => {
        if (isZoomed) return;
        // 전환 직전에 포커스를 받은 컨트롤은 슬라이드가 넘어간 뒤에도 이벤트를 보낸다.
        // 그대로 처리하면 화면 밖 영상이 재생된다. 지금 보고 있는 슬라이드의 조작만 받는다.
        // state 가 아니라 ref 로 비교한다 — 전환 통지는 React 이벤트 밖에서 오므로
        // 렌더가 반영되기 전 구간에도 "지금 어느 슬라이드인가"가 맞아야 한다.
        if (index !== currentIndexRef.current) return;
        e.stopPropagation();
        const video = videoRefs.current[index];
        if (!video) return;
        if (video.paused) {
            playVideo(video);
        } else {
            video.pause();
            setIsPaused(true);
        }
    };

    // 열려 있는 동안에도 슬라이드 리소스가 실패할 수 있다(SAS 만료·네트워크).
    // 실패 항목을 목록에서 빼면 뒤 슬라이드가 앞으로 당겨져 보던 자리가 어긋나므로,
    // 자리는 그대로 두고 그 자리의 내용만 접근 가능한 실패 상태로 바꾼다.
    // index 를 키로 써도 안전하다 — 뷰어가 열려 있는 동안 slides 순서는 고정이다.
    // 실패는 두 곳에서 온다: 뷰어 안에서 직접 관측한 로드 실패(failedSlides)와,
    // 호출부가 URL 을 끝내 확보하지 못했다고 알려준 슬라이드(slide.failed).
    // 후자를 무시하면 그 자리가 영원히 "불러오는 중"으로 남는다.
    const isSlideFailed = useCallback(
        (index) => Boolean(slides[index]?.failed || failedSlides[index] === slideResource(slides[index], index)),
        [slides, failedSlides],
    );

    // 전환 후 포커스가 갈 곳의 종류를 렌더에서 확정한다. 이렇게 해야 아래 effect 의
    // 의존성이 완전해지고(주석으로 규칙을 끄지 않아도 되고), 판단 근거도 한 곳에 남는다.
    const currentSlideIsReadyVideo = useMemo(() => {
        const slide = slides[currentIndex];
        return slide?.type === "video" && Boolean(slide.url) && !isSlideFailed(currentIndex);
    }, [slides, currentIndex, isSlideFailed]);

    const handleSlideError = (index) => {
        const resource = slideResource(slides[index], index);
        setFailedSlides((prev) => (prev[index] === resource ? prev : { ...prev, [index]: resource }));
        // 호출부는 이 신호로 다음에 열 때 그 항목을 제외할 수 있다.
        // 실패한 URL 을 함께 넘긴다 — 호출부가 그 사이 URL 을 교체했다면
        // 이 실패는 이미 지난 리소스의 것이라 무시해야 한다.
        onSlideError?.(index, slides[index]?.url);
    };

    // 전환이 반영된 뒤, 나가는 재생 컨트롤에 있던 포커스를 갈 곳으로 옮긴다.
    // 화면 밖으로 나간 wrapper 는 role/tabIndex 가 사라져 포커스가 갇힌다.
    useEffect(() => {
        if (!focusTransferRef.current) return;
        focusTransferRef.current = false;
        // 영상이 아니거나 아직 볼 수 없는 자리면 항상 존재하는 헤더 컨트롤로 보낸다.
        const target = currentSlideIsReadyVideo
            ? videoWrapperRefs.current[currentIndex]
            : closeButtonRef.current;
        target?.focus();
    }, [currentIndex, currentSlideIsReadyVideo]);

    // 재생 영역은 전체가 하나의 큰 터치 타깃이라 별도 오버레이 버튼을 두지 않는다.
    // 대신 같은 영역을 키보드로도 조작할 수 있게 Enter/Space 를 탭과 같은 경로로 보낸다.
    // Space 는 기본 동작(페이지 스크롤)을 막아야 뷰어가 뒤로 밀리지 않는다.
    const handleVideoKeyDown = (e, index) => {
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
        // stale 포커스면 기본 동작도 막지 않는다. 삼키기만 하고 아무것도 하지 않으면
        // 보이지도 않는 컨트롤이 페이지 스크롤만 죽인다.
        if (index !== currentIndexRef.current) return;
        e.preventDefault();
        handleVideoTap(e, index);
    };

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    const currentSaveKey = slideIdentity(slides[currentIndex], currentIndex);
    const currentSaveState = effectiveSaveStates[currentSaveKey];
    const canSaveCurrent = Boolean(onDownload) && slides[currentIndex]?.downloadable !== false;
    // 실제 종결 결과("done")가 오기 전까지는 어느 단계든 진행 중이다 — 통지 단계에서 잠금을 풀지 않는다.
    // 잠금·중복 차단은 보존된 상태로 첫 렌더부터 적용된다(아래 안내 지연과 무관).
    const isSaving = Boolean(currentSaveState) && currentSaveState.status !== "done";
    const saveFeedback = saveFeedbackOf(currentSaveState);
    // 안내 노드는 **빈 채로 먼저 마운트**되고 한 tick 뒤부터 내용을 싣는다. 호출부가 보존한 상태로 다시 열 때
    // 내용이 담긴 채 마운트되면 live region 이 낭독되지 않을 수 있어서다. 마운트 1회성 준비 플래그일 뿐,
    // 알림 만료·전역 통지 같은 타이머 정책이 아니다.
    const [announceReady, setAnnounceReady] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setAnnounceReady(true), 0);
        return () => clearTimeout(timer);
    }, []);
    const saveStatusText = announceReady && saveFeedback?.role === "status" ? saveFeedback.message : "";
    const saveAlertText = announceReady && saveFeedback?.role === "alert" ? saveFeedback.message : "";

    // 저장은 비동기다. 결과를 모르는 동안 그 슬라이드의 버튼을 잠가 중복 요청을 막고,
    // 결과는 요청한 key 에 기록한다 — 대기 중 슬라이드를 넘겨도 늦게 온 결과는 원래 슬라이드에 남고,
    // 지금 보는 슬라이드에 잘못 붙지 않는다. 실패해도 성공 표시를 하지 않는다.
    const handleDownloadClick = async () => {
        const requestIndex = currentIndexRef.current;
        const slide = slidesRef.current[requestIndex];
        const key = slideIdentity(slide, requestIndex);
        if (slide?.downloadable === false) return;
        if (savingKeysRef.current.has(key) || isSaving) return;

        // 저장 버튼은 곧 disabled 로 잠긴다. disabled 가 되는 요소의 포커스는 브라우저가 body 로 떨어뜨리므로,
        // **이 버튼에 포커스가 있을 때만** 닫기 버튼으로 옮긴다 — 다른 곳의 포커스는 건드리지 않는다.
        if (document.activeElement === saveButtonRef.current) closeButtonRef.current?.focus();

        // 호출부가 상태를 소유하면(saveStates) 기록은 호출부가 한다. 여기서는 잠금과 전달만.
        const setLocal = (state) => {
            if (!saveStates) setLocalSaveStates((prev) => ({ ...prev, [key]: state }));
        };
        savingKeysRef.current.add(key);
        setLocal({ status: "pending" });
        try {
            const result = await onDownload(slide, requestIndex);
            setLocal({ status: "done", result });
        } catch {
            setLocal({ status: "done", result: { via: "none" } });
        } finally {
            savingKeysRef.current.delete(key);
        }
    };

    if (slides.length === 0) return null;

    return (
        <div
            ref={dialogRef}
            className="fullscreen-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={title || "미디어 뷰어"}
        >
            <div className="viewer-inner">
                <div className="viewer-header">
                    <button ref={closeButtonRef} className="viewer-close" type="button" aria-label="닫기" onClick={requestClose}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                    <span className="viewer-counter">
                        {title || `${currentIndex + 1} / ${slides.length}`}
                    </span>
                    {canSaveCurrent ? (
                        <button
                            ref={saveButtonRef}
                            className="viewer-download"
                            type="button"
                            aria-label="저장"
                            aria-busy={isSaving ? "true" : "false"}
                            disabled={isSaving}
                            onClick={handleDownloadClick}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 4v10m0 0l-4-4m4 4l4-4M5 18h14"
                                    stroke="#fff"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    ) : (
                        <div className="viewer-spacer" />
                    )}
                </div>

                {/* 웹 저장(<a download>)은 브라우저가 결과를 알려주지 않으므로 성공을 말하지 않는다.
                    네이티브 저장은 결과 콜백이 오므로 성공·장기 대기·결과 미수신을 status 로, 실패는 alert 로
                    안내한다. 두 노드는 저장 기능이 있는 동안 **처음부터 빈 채로 마운트**돼 있고 role 도 바뀌지 않는다
                    — 내용이 담긴 채 새로 마운트되거나 role 이 바뀌는 노드는 보조기기가 안내를 놓친다.
                    빈 상태는 SCSS 의 :empty 로 크기만 0 이 되며 접근성 트리에서 빠지지 않는다.
                    현재 슬라이드 key 의 상태만 보인다. */}
                {onDownload && (
                    <>
                        <p className="viewer-save-status" role="status">{saveStatusText}</p>
                        <p className="viewer-save-error" role="alert">{saveAlertText}</p>
                    </>
                )}

                <Swiper
                    className="viewer-body"
                    modules={[Zoom]}
                    zoom={{ maxRatio: 2, minRatio: 1, toggle: true }}
                    initialSlide={initialIndex}
                    spaceBetween={0}
                    slidesPerView={1}
                    onSwiper={(swiper) => { swiperRef.current = swiper; }}
                    onSlideChange={(swiper) => {
                        // 나가는 슬라이드의 재생 컨트롤에 포커스가 있었을 때만 이어준다.
                        // 닫기·저장 등 다른 컨트롤에 있었다면 빼앗지 않는다.
                        // 예외: 저장 버튼에 포커스가 있는데 다음 슬라이드가 저장 불가(downloadable:false)면
                        // 버튼이 언마운트돼 포커스가 body 로 떨어지므로 같은 경로로 dialog 안에 붙잡아 둔다.
                        const active = document.activeElement;
                        const nextSlide = slidesRef.current[swiper.activeIndex];
                        focusTransferRef.current =
                            active === videoWrapperRefs.current[currentIndexRef.current] ||
                            (active === saveButtonRef.current && nextSlide?.downloadable === false);
                        // 전환 통지는 React 이벤트 밖(터치·애니메이션 콜백)에서 온다.
                        // state 반영을 기다리면 그 사이에 도착한 이전 요청·조작이
                        // 여전히 이전 슬라이드로 귀속된다. 기준값부터 먼저 옮긴다.
                        currentIndexRef.current = swiper.activeIndex;
                        setCurrentIndex(swiper.activeIndex);
                    }}
                    onZoomChange={(_swiper, scale) => setIsZoomed(scale > 1)}
                >
                    {slides.map((slide, index) => (
                        <SwiperSlide key={index} className="viewer-slide">
                            <div className="swiper-zoom-container">
                                {/* 호출부가 슬라이드 자리를 먼저 잡고 URL 을 나중에 채우는 경우가 있다.
                                    url 이 빈 <img>/<video> 는 브라우저가 현재 페이지를 다시 받아
                                    깨진 미디어로 보이므로, 채워지기 전에는 자리표시자만 둔다. */}
                                {isSlideFailed(index) ? (
                                    <span
                                        className="viewer-slide-error"
                                        role="img"
                                        aria-label={`${slide.type === "video" ? "동영상" : "이미지"} ${index + 1} 불러오기 실패`}
                                    >
                                        불러올 수 없어요
                                    </span>
                                ) : !slide.url ? (
                                    <span
                                        className="viewer-slide-pending"
                                        // status 는 live region 이다. 화면 밖 형제까지 주면
                                        // 보고 있지도 않은 슬라이드의 로딩이 한꺼번에 낭독된다.
                                        role={index === currentIndex ? "status" : undefined}
                                        aria-label={`${slide.type === "video" ? "동영상" : "이미지"} ${index + 1} 불러오는 중`}
                                    >
                                        {/* live region 은 이름이 아니라 내용 변화를 읽는다.
                                            비어 있으면 role 만 있고 낭독할 것이 없다. */}
                                        불러오는 중
                                    </span>
                                ) : slide.type === "video" ? (
                                    <div
                                        ref={(el) => {
                                            if (el) videoWrapperRefs.current[index] = el;
                                            else delete videoWrapperRefs.current[index];
                                        }}
                                        className="video-slide-wrapper"
                                        // 화면 밖 슬라이드는 조작해도 무시된다(현재 슬라이드만 동작).
                                        // 그렇다면 접근성 트리에도 컨트롤로 남기지 않는다 — 남기면
                                        // 스크린리더에 슬라이드 수만큼의 재생 버튼이 읽힌다.
                                        role={index === currentIndex ? "button" : undefined}
                                        tabIndex={index === currentIndex ? 0 : undefined}
                                        aria-label={
                                            index === currentIndex
                                                ? (isPaused ? "동영상 재생" : "동영상 일시정지")
                                                : undefined
                                        }
                                        onClick={(e) => handleVideoTap(e, index)}
                                        onKeyDown={(e) => handleVideoKeyDown(e, index)}
                                    >
                                        <video
                                            // URL 이 바뀌면 요소를 갈아 끼운다. 같은 DOM 을 재사용하면
                                            // 옛 URL 의 늦은 error 가 새 URL 의 실패로 기록된다.
                                            key={slide.url}
                                            ref={(el) => { videoRefs.current[index] = el; }}
                                            data-testid={`viewer-video-${index}`}
                                            src={slide.url}
                                            // 초기 슬라이드의 URL 이 늦게 와도, 그 사이 사용자가
                                            // 다른 슬라이드로 넘어갔다면 자동재생 대상이 아니다.
                                            autoPlay={index === initialIndex && index === currentIndex}
                                            loop
                                            muted={isMuted}
                                            playsInline
                                            onError={() => handleSlideError(index)}
                                        />
                                        {isPaused && index === currentIndex && !isZoomed && (
                                            <div className="video-paused-indicator">
                                                <span>&#9654;</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <img
                                        key={slide.url}
                                        src={slide.url}
                                        alt={`이미지 ${index + 1}`}
                                        onError={() => handleSlideError(index)}
                                    />
                                )}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* URL 이 아직 없으면 조작할 대상 자체가 없다. 눌러도 아무 일이 없는
                    seek·음소거를 먼저 띄우면 포커스 순서만 어지럽힌다. */}
                {slides[currentIndex]?.type === "video" && slides[currentIndex]?.url && !isSlideFailed(currentIndex) && !isZoomed && (
                    <div className="video-seek-container">
                        <div className="video-seek-time">
                            <span>{formatTime(currentTime)}</span>
                            <div className="video-seek-right">
                                <button
                                    className="mute-toggle-btn"
                                    onClick={() => setIsMuted((prev) => !prev)}
                                    aria-label={isMuted ? "음소거 해제" : "음소거"}
                                    type="button"
                                >
                                    {isMuted ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <line x1="23" y1="9" x2="17" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                            <line x1="17" y1="9" x2="23" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    )}
                                </button>
                                <span>{formatTime(duration)}</span>
                            </div>
                        </div>
                        <div className="video-seek-bar" onClick={handleSeek}>
                            <div
                                className="video-seek-fill"
                                style={{ width: `${progress * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {slides.length > 1 && !isZoomed && (
                    <div className="viewer-dots">
                        {slides.map((_, index) => (
                            <span
                                key={index}
                                className={`dot ${
                                    index === currentIndex ? "active" : ""
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MediaViewer;
